import React, {
  useEffect, useRef, useState,
} from 'react';
import {
  ArrowLeft, CheckCircle2, Copy, ExternalLink, Landmark, Loader2, ShieldCheck,
  Wallet,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AccessBlockedNotice from '../../components/AccessBlockedNotice';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client, { createIdempotencyKey } from '../../lib/api';
import PaymentMethodLogo from '../../components/PaymentMethodLogo';
import useChatFreePage from '../../hooks/useChatFreePage';
import {
  SHOWCASE_UNAVAILABLE_TITLE, isShowcaseMethod, showcaseHasCustomMessage,
  showcaseMessage,
} from '../../lib/showcaseMethod';
import { accountHook } from '../../lib/domHooks';
import { defaultPayerName, payerNameIsValid } from '../../lib/payerName';

// Sağlayıcının cevabı bu işleme atanmış bir hesap taşıyor mu? Hesap varsa
// oyuncuyu hosted sayfaya götürmek ekrandaki IBAN'ı kaybettirir; yoksa
// ekranda bağlantıdan başka bir şey kalmaz.
const hasBankAccount = (payment) => {
  const account = payment?.bank_account;
  if (!account) return false;
  return Boolean(account.iban || account.holder || account.bank);
};

const DepositPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const methodId = searchParams.get('method') || '';
  const amount = Number(searchParams.get('amount'));
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [paidNotified, setPaidNotified] = useState(false);
  const requestKey = useRef(null);
  const { setBalance, user } = useAuth();
  const { toast } = useToast();

  // Sohbet düğmesi ekranın sağ alt köşesindedir ve bu sayfada oyuncunun
  // basması gereken şeyin üstüne oturur: "… ile Devam Et" düğmesi, oluşan
  // IBAN/hesap bilgisi ve kopyala düğmeleri tam oraya düşer. Ödemenin
  // ortasında yanlış yere basmak paranın yanlış hesaba gitmesi demektir, bu
  // yüzden gizleme sayfa boyunca sürer. Üç kapı da (sitenin LiveChat'i,
  // panelden girilen özel koddan gelen widget, koşulsuz CSS kuralı)
  // `useChatFreePage` içindedir; sayfadan çıkınca destek aynen geri gelir.
  useChatFreePage();

  useEffect(() => {
    client.get('/payments/methods', { params: { direction: 'deposit' } })
      .then(({ data }) => {
        const selectedMethod = (data.items || []).find((item) => item.id === methodId);
        setMethod(selectedMethod || null);
      })
      .catch(() => setMethod(null))
      .finally(() => setLoading(false));
  }, [methodId]);

  const minimum = Number(method?.min_amount);
  const maximum = Number(method?.max_amount);
  const validAmount = Number.isFinite(amount)
    && Number.isFinite(minimum)
    && Number.isFinite(maximum)
    && amount >= minimum
    && amount <= maximum;
  const isWizkasa = method?.provider === 'wizkasa';
  const isHemenSystem = method?.provider === 'hemensystem';
  // Aynı sağlayıcının iki akışı vardır: havale IBAN döndürür ve oyuncu
  // transferi kendi bankasından yapar, kart ödemesi ise sağlayıcının 3D
  // sayfasında tamamlanır. Ayrım yöntem satırındadır.
  const isHemenSystemCard = isHemenSystem
    && method?.id === 'hemensystem-creditcard';
  const isHemenSystemBank = isHemenSystem && !isHemenSystemCard;
  const isHera = method?.provider === 'hera';
  // Payurus ödemesi tamamen sağlayıcının kendi sayfasında tamamlanır: cevabın
  // taşıdığı tek şey o sayfanın adresidir, IBAN/kart bilgisi orada sorulur.
  const isPayurus = method?.provider === 'payurus';
  // UltraPay'de akış yönteme göre ikiye ayrılır: H2H havale (42) alıcı
  // IBAN'ı doğrudan cevapta döndürür ve burada gösterilir, kalan yöntemler
  // sağlayıcının ödeme sayfasına yönlendirir.
  const isUltraPay = method?.provider === 'ultrapay';
  const ultrapayIsH2H = isUltraPay && method?.id === 'ultrapay-42';
  // Link Havale'de bütün yöntemler (havale, kart, Papara, kripto) sağlayıcının
  // kendi ödeme sayfasında tamamlanır: cevabın taşıdığı tek şey o sayfanın
  // adresidir ve bağlantı 30 dakika geçerlidir, tek kullanımlıktır.
  const isLinkHavale = method?.provider === 'linkhavale';
  // VEVO PAY yatırımda bu işleme özel IBAN'ı **ya da** yalnızca ödeme
  // sayfasının adresini döndürür; ayrım cevabındadır. Hesap döndüyse ikisi de
  // gösterilir ve otomatik yönlendirme yapılmaz — ekranda duran hesap bilgisi
  // kaybolurdu (Hemen Havale'deki gerekçenin aynısı). Hosted akışta
  // (panelden `vevopay_hosted_deposit` seçili ya da oyuncunun adı kayıtlı
  // değil) ekranda tutulacak bir hesap bilgisi **yoktur**: orada yönlendirme
  // yapılmayınca oyuncu ödeme sayfasına ancak "bağlantıyı kullanın" satırına
  // dokunarak gidebiliyordu.
  const isVevoPay = method?.provider === 'vevopay';
  // HızlıcaParalar'da bütün yöntemler (havale, FAST, kart, kripto)
  // sağlayıcının kendi sayfasında tamamlanır ve **tutar orada yazılır**:
  // yatırım isteğimiz tutar taşımaz, bizim ekranımızdaki değer bir taleptir.
  // Bize dönen tek bilgi o sayfanın adresidir.
  const isHizlicaParalar = method?.provider === 'hizlicaparalar';
  const hizlicaIsCard = isHizlicaParalar
    && method?.id === 'hizlicaparalar-creditcard';
  // Hızlı Havale de VEVO PAY gibi iki akış taşır: sağlayıcı işleme bir banka
  // hesabı atar (varsayılan) ya da panelden seçilen ödeme sayfası akışında
  // yalnızca o sayfanın adresini döndürür. Ayrım **cevabın kendisindedir**,
  // sağlayıcı adında değil: adı kayıtlı olmayan üyede ödeme sayfası akışı
  // kullanılamadığı için aynı kurulumda iki cevap birden görülür.
  const isHizliHavale = method?.provider === 'hizlihavale';
  // Zelvoq'un havale yöntemi panelden iki şekilde açılabilir: sağlayıcı işleme
  // bir banka hesabı atar (``show_bank``) ya da kendi ödeme sayfasını verir.
  // Kart, Papara ve kripto her zaman ödeme sayfasıdır. Ayrım burada da
  // **cevabın kendisindedir**, sağlayıcı adında değil.
  const isZelvoq = method?.provider === 'zelvoq';
  // CryptoBox'ta yatırım bir **adrestir**: zincir yönteminde üyeye özel adres
  // ekranımızda çizilir, "tüm zincirler" yönteminde sağlayıcının ödeme sayfası
  // açılır. Ayrım yine cevabın kendisindedir (``crypto`` ya da ``link``).
  const isCryptoBox = method?.provider === 'cryptobox';
  // CashMoney Pay (H2H) işleme bir banka hesabı atar; sonuç callback ile
  // gelir. Ödeme sayfası adresi dönerse oraya yönlendirilir.
  const isCashMoney = method?.provider === 'cashmoney';
  // Paylux'te tek akış vardır: sağlayıcı işleme bir IBAN **ve ödenecek
  // tutarı** atar, ödeme sayfası yoktur. Tutar talep edilenden farklı
  // olabilir (sağlayıcı havaleyi onunla eşleştirir) ve oyuncu ekranda o
  // tutarı görmelidir — talep ettiği tutarı yollarsa ödeme eşleşmez.
  const isPaylux = method?.provider === 'paylux';
  // ExPay iki akış taşır (panelden seçilir): sağlayıcı işleme bir IBAN ve
  // alıcı adı atar (H2H) ya da yalnızca ödeme sayfasının adresini döndürür.
  // Ayrım **cevabın kendisindedir**, sağlayıcı adında değil.
  const isExpay = method?.provider === 'expay';
  const expayShowsAccount = isExpay && hasBankAccount(transaction?.payment);
  // PrimeVpay'de ödeme her zaman sağlayıcının sayfasında tamamlanır: atanan
  // IBAN ve "ödemeyi yaptım" düğmesi oradadır, operasyon ekibi havaleyi o
  // işaretle kontrol eder (bkz. backend/services/primevpay.py).
  const isPrimevpay = method?.provider === 'primevpay';
  // PrimeVpay havaleyi gönderen hesabın sahibiyle eşleştirir ve adı
  // zorunlu tutar. Kayıtlı ad soyad varsa önceden dolar; KYC manuel
  // moddaysa kayıt formu ad sormaz ve oyuncu burada yazar.
  const [payerName, setPayerName] = useState('');
  const payerNameValid = payerNameIsValid(payerName);
  const defaultPayer = defaultPayerName(user);
  useEffect(() => {
    // Oyuncu yazmaya başladıysa ezilmez; yalnızca boş alan doldurulur.
    if (defaultPayer) setPayerName((prev) => prev || defaultPayer);
  }, [defaultPayer]);
  // Hosted akış yöntem satırının kendisinden bellidir ("Kripto Para
  // (CryptoBox)"); cevaba bakmak yetmez, düğmenin yazısı kayıt açılmadan
  // **önce** de doğru olmalıdır — "Ödemeyi Yaptım" yazan bir düğme oyuncuyu
  // sağlayıcının sayfasına göndereceğini söylemiyordu.
  const cryptoboxIsHosted = isCryptoBox && method?.id === 'cryptobox-checkout';
  const cryptoboxAddress = isCryptoBox ? transaction?.payment?.crypto : null;
  // Hera'nın havale akışı (H2H) IBAN'ı doğrudan döndürür; kart akışları
  // yalnızca sağlayıcının barındırdığı ödeme sayfasına bağlantı verir.
  const heraMethodType = isHera ? (method?.hera_method_type || 'BANK') : '';
  const heraIsBankTransfer = isHera && heraMethodType === 'BANK';
  // "Ödemeyi yaptım" yalnızca oyuncunun kendi bankasından transfer yaptığı
  // akışlarda anlamlıdır; kart ödemesinde sonucu 3D akışının kendisi bildirir.
  // VEVO PAY'de bildirim sağlayıcıya da iletilir
  // (`POST /merchant/api/deposit/{id}/claimed`) ve ardından durum sorulur.
  // HızlıcaParalar'da bildirim **yalnızca yereldir**: sağlayıcının durum
  // sorgulama ucu yoktur, para yine callback ile girer. Yine de oyuncuya
  // kapanan bir adım verir ve yöneticiye "oyuncu ödediğini söylüyor"
  // bilgisini bırakır. Kart ödemesinde anlamsızdır — sonucu 3D akışı bildirir.
  // Hızlı Havale'de bildirim yerel bir işarettir (sağlayıcıya iletilecek uç
  // yoktur) ama ardından durum sorulur: sonucu zaten her zaman o cevap
  // belirler.
  // Zelvoq'ta bildirim yerel bir işarettir (sağlayıcıya iletilecek uç yoktur)
  // ama ardından durum sorulur. Yalnızca hesap akışında anlamlıdır: ödeme
  // sayfası akışında sonucu sayfanın kendisi bildirir.
  const zelvoqShowsAccount = isZelvoq && hasBankAccount(transaction?.payment);
  const supportsPaidNotification = isHemenSystemBank || heraIsBankTransfer
    || isVevoPay || isHizliHavale || zelvoqShowsAccount || isPaylux
    || expayShowsAccount
    || Boolean(cryptoboxAddress?.ready)
    || (isHizlicaParalar && !hizlicaIsCard);
  // Sonucu callback belirleyen sağlayıcı: talep oluşturulduktan sonra
  // ekranda kalınır, yönetici onayı beklenmez.
  const isApiProvider = isWizkasa || isHemenSystem || isHera || isPayurus
    || isUltraPay || isLinkHavale || isVevoPay || isHizlicaParalar
    || isHizliHavale || isZelvoq || isCryptoBox || isCashMoney || isPaylux
    || isExpay || isPrimevpay;
  const providerPayment = transaction?.payment || null;
  // Gönderilecek coin miktarı yalnızca adres hazırken anlamlıdır; adres
  // beklerken miktarı büyük puntoyla göstermek, gönderilecek yeri olmayan
  // bir sayı yazmak olurdu.
  const cryptoQuote = (isCryptoBox && providerPayment?.crypto?.ready
    && providerPayment.crypto.quote?.amount)
    ? providerPayment.crypto.quote : null;
  // Wizkasa LINK kanalında, Hemen Havale'de, Hera'nın kart akışlarında ve
  // Payurus'ta ödeme sağlayıcının hosted sayfasında tamamlanır.
  const hostedPaymentUrl = isApiProvider ? (providerPayment?.link || '') : '';
  // Ekranda hesap ya da adres yoksa ödeme yalnızca sağlayıcının sayfasında
  // yapılır. O zaman ana düğme o sayfayı açar: "Ödemeyi Yaptım" yazan düğme
  // oyuncuyu sağlayıcının sitesine götürüyor ve ne yapacağını şaşırtıyordu.
  const paysOnHostedPage = Boolean(transaction && hostedPaymentUrl)
    && !hasBankAccount(providerPayment) && !cryptoboxAddress;

  const confirmPayment = async () => {
    if (!method || !validAmount || busy || (transaction && !isApiProvider)) return;
    if (isApiProvider && transaction) {
      if (hostedPaymentUrl) window.location.assign(hostedPaymentUrl);
      return;
    }
    if (isPrimevpay && !payerNameValid) {
      toast({
        title: 'Ad soyad gerekli',
        description: 'Havaleyi göndereceğiniz hesabın sahibinin adını ve soyadını yazın.',
      });
      return;
    }
    setBusy(true);
    try {
      requestKey.current ||= createIdempotencyKey();
      const { data } = await client.post(
        '/finance/deposit',
        {
          method_id: method.id,
          method_name: method.name,
          amount,
          ...(isPrimevpay ? { payer_name: payerName.trim() } : {}),
        },
        { headers: { 'Idempotency-Key': requestKey.current } },
      );
      requestKey.current = null;
      setBalance(data.balance);
      const created = data.payment || data.transaction?.payment;
      setTransaction({ ...data.transaction, payment: created });
      // Ekranda gösterilecek bir hesap yoksa yönlendirmeyi bekletecek bir şey
      // de yoktur.
      const vevoPayShowsAccount = isVevoPay && hasBankAccount(created);
      // Ayrım sağlayıcı adına değil **hesabın varlığına** bakılarak yapılır:
      // aynı sağlayıcı bir üyede hesap, diğerinde ödeme sayfası döndürür.
      const hizliHavaleShowsAccount = isHizliHavale && hasBankAccount(created);
      const zelvoqAccount = isZelvoq && hasBankAccount(created);
      const cashMoneyAccount = isCashMoney && hasBankAccount(created);
      const expayAccount = isExpay && hasBankAccount(created);
      if (
        isHemenSystemBank || heraIsBankTransfer || vevoPayShowsAccount
        || hizliHavaleShowsAccount || zelvoqAccount || cashMoneyAccount
        || isPaylux || expayAccount
      ) {
        // Sağlayıcı IBAN'ı doğrudan döndürür; oyuncuyu hosted sayfaya
        // zorla götürmek ekrandaki hesap bilgisini kaybettirirdi.
        toast({
          title: 'Ödeme bilgileri oluşturuldu',
          description: 'Transferi yalnızca aşağıdaki hesaba yapın.',
        });
      } else if (isWizkasa || isHera || isPayurus || isUltraPay
        || isLinkHavale || isHemenSystemCard || isVevoPay
        || isHizlicaParalar || isHizliHavale || isZelvoq || isCryptoBox
        || isCashMoney || isExpay || isPrimevpay) {
        const link = data.payment_url || data.payment?.link || '';
        if (link) {
          toast({
            title: 'Ödeme sayfasına yönlendiriliyorsunuz',
            description: 'Sayfa açılmazsa aşağıdaki bağlantıyı kullanın.',
          });
          window.location.assign(link);
        } else {
          toast({
            title: 'Ödeme bilgileri oluşturuldu',
            description: 'Transferi yalnızca aşağıdaki hesaba yapın.',
          });
        }
      } else {
        toast({
          title: 'Ödemeniz bildirildi',
          description: 'Yatırımınız yönetici onayına gönderildi.',
        });
      }
    } catch (error) {
      toast({
        title: 'Yatırım bildirilemedi',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setBusy(false);
    }
  };

  // Havaleyi yaptığını bildirir. Bakiyeyi değiştirmez — para sağlayıcının
  // bildirimiyle girer; bu yalnızca oyuncuya kapanan bir adım verir ve
  // bildirim hiç gelmezse yöneticiye "oyuncu ödediğini söylüyor" bilgisini
  // bırakır.
  const notifyPaid = async () => {
    if (!transaction || busy || paidNotified) return;
    setBusy(true);
    try {
      const { data } = await client.post(
        `/finance/deposit/${transaction.id}/odedim`,
      );
      if (Number.isFinite(Number(data.balance))) setBalance(Number(data.balance));
      if (data.transaction) {
        setTransaction({
          ...data.transaction,
          payment: data.transaction.payment || providerPayment,
        });
      }
      setPaidNotified(true);
      toast({
        title: 'Bildiriminiz alındı',
        description: isVevoPay
          ? (data.credited || data.transaction?.status === 'onaylandi'
            ? 'VEVO PAY ödemeyi onayladı; bakiyeniz güncellendi.'
            : data.provider_checked
              ? 'Bildiriminiz sağlayıcıya iletildi. Havaleniz doğrulandığında bakiyeniz otomatik yüklenecek.'
              : 'Bildiriminiz kaydedildi. Havaleniz ulaştığında bakiyeniz otomatik yüklenecek.')
          : isHera
          ? (data.credited || data.transaction?.status === 'onaylandi'
            ? 'Hera ödemeyi onayladı; bakiyeniz güncellendi.'
            : data.applied || (
              data.transaction?.status
              && data.transaction.status !== 'beklemede'
            )
              ? 'Hera işlem sonucunu bildirdi; yatırım durumunuz güncellendi.'
              : data.provider_checked
                ? 'Hera durumu sorgulandı. Havaleniz onaylandığında bakiyeniz otomatik yüklenecek.'
                : 'Bildiriminiz kaydedildi. Hera sorgusu şu an yanıt vermedi; sağlayıcı bildirimi bekleniyor.')
          : 'Havaleniz ulaştığında bakiyeniz otomatik yüklenecek.',
      });
    } catch (error) {
      toast({
        title: 'Bildirim gönderilemedi',
        description: error.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setBusy(false);
    }
  };

  // Kısıt talep açılırken sunucuda da denetlenir; bu kontrol yalnızca
  // oyuncunun ödeme ekranına kadar gelip 403 ile karşılaşmasını önler.
  if (user?.deposit_blocked) {
    return (
      <AccessBlockedNotice
        title="Para yatırma işleminiz kısıtlanmıştır"
        description={'Hesabınız yönetici tarafından para yatırma işlemine '
          + 'kapatılmıştır. Yeni bir yatırım talebi oluşturamazsınız.'}
      />
    );
  }

  if (loading) {
    return (
      <div className="pusu-panel py-16 text-center text-sm text-gray-400" {...accountHook('loading')}>
        <Loader2 size={20} className="inline animate-spin mr-2" />
        Ödeme bilgileri yükleniyor...
      </div>
    );
  }

  // Göstermelik firmanın arkasında hesap yoktur. Yöntem penceresi buraya
  // geçirmez ama adres elle yazılabilir ya da eski bir sekmeden açılabilir;
  // ekran o zaman boş bir hesap kartı çizerdi. Asıl kapı sunucudadır.
  if (method && isShowcaseMethod(method)) {
    return (
      <div className="pusu-panel p-6 sm:p-8 text-center max-w-2xl mx-auto" {...accountHook('notice')}>
        {!showcaseHasCustomMessage(method) && (
          <div className="text-lg font-bold mb-2">
            {SHOWCASE_UNAVAILABLE_TITLE}
          </div>
        )}
        <p className="text-sm text-gray-400 mb-5">
          {showcaseMessage(method)}
        </p>
        <button
          type="button"
          className="gold-btn px-5 py-3 rounded-xl"
          onClick={() => navigate('/hesap/para-yatir', { replace: true })}
        >
          Yatırım Yöntemlerine Dön
        </button>
      </div>
    );
  }

  if (!method || !validAmount) {
    return (
      <div className="pusu-panel p-6 sm:p-8 text-center" {...accountHook('empty')}>
        <div className="text-lg font-bold mb-2">Ödeme bilgileri bulunamadı</div>
        <p className="text-sm text-gray-400 mb-5">
          Yatırım yöntemi veya tutar geçerli değil. Lütfen yeniden seçim yapın.
        </p>
        <button
          type="button"
          className="gold-btn px-5 py-3 rounded-xl"
          onClick={() => navigate('/hesap/para-yatir', { replace: true })}
        >
          Yatırım Yöntemlerine Dön
        </button>
      </div>
    );
  }

  if (transaction && !isApiProvider) {
    return (
      <div className="pusu-panel p-6 sm:p-8 text-center max-w-2xl mx-auto" data-bhs-status="notified" {...accountHook('panel')}>
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-400/10 text-emerald-400 flex items-center justify-center mb-4">
          <CheckCircle2 size={34} aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-black gold-text" {...accountHook('title')}>Ödemeniz Bildirildi</h1>
        <p className="text-sm text-gray-400 mt-2">
          {amount.toLocaleString('tr-TR')} ₺ tutarındaki yatırımınız yönetici
          kontrolüne gönderildi. Onaylandığında bakiyeniz güncellenecektir.
        </p>
        <div className="pusu-card p-3 mt-5 text-xs text-gray-400 break-all" {...accountHook('detail', 'islem-no')}>
          İşlem no: {transaction.id}
        </div>
        <button
          type="button"
          className="gold-btn w-full sm:w-auto px-6 py-3 rounded-xl mt-5"
          onClick={() => navigate('/hesap/para-yatir?view=history', { replace: true })}
        >
          Yatırımlarımı Gör
        </button>
      </div>
    );
  }

  const isBank = method.method_type === 'bank';

  return (
    <div className="max-w-3xl mx-auto" data-bhs-status={transaction ? 'created' : 'draft'}>
      <button
        type="button"
        className="provider-chip mb-4 flex items-center gap-2"
        {...accountHook('back')}
        onClick={() => navigate('/hesap/para-yatir')}
      >
        <ArrowLeft size={15} aria-hidden="true" />
        Yöntemlere Dön
      </button>

      <div className="pusu-panel overflow-hidden" {...accountHook('panel')}>
        <div className="p-5 sm:p-6 border-b border-white/5" {...accountHook('page-header')}>
          <div className="flex items-center gap-3">
            <PaymentMethodLogo method={method} size={52} />
            <div>
              <div className="text-xs text-gray-500 uppercase">Ödeme yöntemi</div>
              <h1 className="text-xl font-black" {...accountHook('title')}>{method.name}</h1>
            </div>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {/* Kripto adresinde "2.500 ₺" tek başına bir şey söylemez: oyuncu
              coin gönderir. Miktar hesaplanabildiyse başlık odur, TL karşılığı
              altına düşer. */}
          {cryptoQuote ? (
            <div className="pusu-card p-4 text-center mb-5" {...accountHook('amount')}>
              <div className="text-xs text-gray-400 uppercase">
                Gönderilecek miktar
              </div>
              <div className="text-3xl font-black text-white mt-1 break-all">
                {cryptoQuote.amount} {cryptoQuote.asset}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                ≈ {amount.toLocaleString('tr-TR')} ₺
                {cryptoQuote.unit_price_try && (
                  <> · 1 {cryptoQuote.asset} ={' '}
                    {Number(cryptoQuote.unit_price_try).toLocaleString('tr-TR', {
                      maximumFractionDigits: 2,
                    })} ₺
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="pusu-card p-4 text-center mb-5" {...accountHook('amount')}>
              <div className="text-xs text-gray-400 uppercase">Gönderilecek tutar</div>
              <div className="text-3xl font-black text-white mt-1">
                {amount.toLocaleString('tr-TR')} ₺
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-3" {...accountHook('header')}>
            {isBank ? <Landmark size={18} /> : <Wallet size={18} />}
            <h2 className="font-bold">
              {isPayurus || isLinkHavale || isHizlicaParalar || isPrimevpay
                || (isUltraPay && !ultrapayIsH2H)
                || (isHizliHavale && !hasBankAccount(providerPayment))
                || (isZelvoq && !hasBankAccount(providerPayment))
                || (isCryptoBox && !providerPayment?.crypto)
                || (isCashMoney && providerPayment && !hasBankAccount(providerPayment))
                ? 'Ödeme sayfası'
                : isBank ? 'Banka hesap bilgileri' : 'Kripto ödeme bilgileri'}
            </h2>
          </div>

          <div className="space-y-2" {...accountHook('payment-details')}>
            {isPayurus ? (
              <PayurusPaymentDetails payment={providerPayment} />
            ) : isPrimevpay ? (
              <PrimevpayPaymentDetails
                payment={providerPayment}
                payerName={payerName}
                payerNameValid={payerNameValid}
                onPayerNameChange={setPayerName}
              />
            ) : isVevoPay ? (
              <VevoPayPaymentDetails payment={providerPayment} />
            ) : isLinkHavale ? (
              <LinkHavalePaymentDetails payment={providerPayment} />
            ) : isHizlicaParalar ? (
              <HizlicaParalarPaymentDetails payment={providerPayment} />
            ) : isHizliHavale ? (
              <HizliHavalePaymentDetails payment={providerPayment} />
            ) : isZelvoq ? (
              <ZelvoqPaymentDetails payment={providerPayment} />
            ) : isPaylux ? (
              <PayluxPaymentDetails payment={providerPayment} />
            ) : isExpay ? (
              <ExpayPaymentDetails payment={providerPayment} />
            ) : isCashMoney ? (
              <CashMoneyPaymentDetails payment={providerPayment} />
            ) : isCryptoBox ? (
              <CryptoBoxPaymentDetails
                payment={providerPayment}
                transactionId={transaction?.id}
                onRefreshed={(fresh) => setTransaction((prev) => ({
                  ...prev, ...fresh, payment: fresh.payment || prev?.payment,
                }))}
              />
            ) : isUltraPay ? (
              <UltraPayPaymentDetails
                payment={providerPayment}
                bankTransfer={ultrapayIsH2H}
              />
            ) : isHemenSystem ? (
              <HemenSystemPaymentDetails
                payment={providerPayment}
                creditCard={isHemenSystemCard}
              />
            ) : isHera ? (
              <HeraPaymentDetails
                payment={providerPayment}
                bankTransfer={heraIsBankTransfer}
              />
            ) : isWizkasa ? (
              <WizkasaPaymentDetails
                payment={providerPayment}
                methodName={method.name}
              />
            ) : isBank ? (
              <>
                <Detail
                  label="Banka"
                  value={providerPayment?.bank_name || method.bank_name || method.name}
                />
                <Detail
                  label="Hesap sahibi"
                  value={providerPayment?.account_holder || method.account_holder}
                />
                <Detail label="IBAN" value={providerPayment?.iban || method.iban} copy />
                {providerPayment?.expires_at && (
                  <Detail
                    label="Son kullanım"
                    value={new Date(providerPayment.expires_at).toLocaleString('tr-TR')}
                  />
                )}
              </>
            ) : (
              <>
                <Detail
                  label="Kripto / Ağ"
                  value={`${method.name}${method.network ? ` · ${method.network}` : ''}`}
                />
                <Detail label="Cüzdan adresi" value={method.wallet_address} copy />
              </>
            )}
          </div>

          {hostedPaymentUrl && !paysOnHostedPage && (
            <a
              className="mt-4 pusu-card p-4 text-sm text-gray-300 flex items-center gap-2 break-all"
              {...accountHook('payment-link')}
              href={hostedPaymentUrl}
              rel="noreferrer"
            >
              <ExternalLink size={16} className="shrink-0" aria-hidden="true" />
              Ödeme sayfası açılmadıysa bu bağlantıyı kullanın.
            </a>
          )}

          {!isApiProvider && (providerPayment?.description || method.instructions) && (
            <div className="mt-4 pusu-card p-4 text-sm text-gray-300 whitespace-pre-wrap" {...accountHook('instructions')}>
              {providerPayment?.description || method.instructions}
            </div>
          )}

          <div className="flex items-start gap-3 mt-5 p-4 rounded-xl bg-amber-400/5 border border-amber-400/20" {...accountHook('notice')}>
            <ShieldCheck size={20} className="text-amber-300 shrink-0 mt-0.5" />
            <p className="text-xs text-gray-300">
              {isApiProvider
                ? (transaction
                  ? (paysOnHostedPage
                    ? 'Ödemeyi “Ödeme İçin Tıkla” düğmesiyle açılan sayfada tamamlayın. Ödeme onaylandığında bakiyeniz otomatik yüklenir; ayrıca bildirim yapmanıza gerek yoktur.'
                    : isCryptoBox && cryptoboxAddress
                    ? 'Gönderimi yaptıktan sonra “Ödemeyi Yaptım” düğmesine basın. Bakiyeniz blokzincir onayı tamamlandığında otomatik yüklenir; süre ağa göre değişir.'
                    : supportsPaidNotification
                    ? 'Havaleyi yaptıktan sonra “Ödemeyi Yaptım” düğmesine basın. Bakiyeniz banka transferi ulaştığında otomatik yüklenir; bekleme süresi bankanıza göre değişir.'
                    : 'Ödemeniz tamamlandığında bakiyeniz otomatik güncellenir; ayrıca bildirim yapmanıza gerek yoktur.')
                  : 'Devam ettiğinizde bu işleme özel ödeme bilgileri oluşturulur.')
                : 'Ödemeyi tamamladıktan sonra aşağıdaki butona bir kez basın. Bakiyeniz doğrulanmış onaydan sonra hesabınıza eklenecektir.'}
            </p>
          </div>

          {paysOnHostedPage ? (
            <div className="mt-5 space-y-2" {...accountHook('actions')}>
              <a
                className="gold-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2"
                {...accountHook('submit')}
                href={hostedPaymentUrl}
                rel="noreferrer"
              >
                <ExternalLink size={16} aria-hidden="true" />
                Ödeme İçin Tıkla
              </a>
              <button
                type="button"
                className="provider-chip w-full justify-center py-3"
                onClick={() => navigate('/hesap/para-yatir?view=history', { replace: true })}
              >
                Yatırımlarımı Gör
              </button>
            </div>
          ) : supportsPaidNotification && transaction ? (
            // Havale bittiğinde oyuncunun basacağı bir adım olmalı: bakiye
            // sağlayıcının bildirimiyle zaten yükleniyor ama ekranda kapanan
            // bir eylem olmadan oyuncu "bir şeyi eksik mi bıraktım" diye
            // dönüyordu. Hera'da düğme ayrıca resmî durum ucunu hemen sorgular.
            <div className="mt-5 space-y-2" {...accountHook('actions')}>
              <button
                type="button"
                className="gold-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"
                {...accountHook('submit')}
                disabled={busy || paidNotified}
                onClick={notifyPaid}
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                {paidNotified ? (
                  <>
                    <CheckCircle2 size={16} aria-hidden="true" />
                    Bildiriminiz Alındı
                  </>
                ) : 'Ödemeyi Yaptım'}
              </button>
              <button
                type="button"
                className="provider-chip w-full justify-center py-3"
                onClick={() => navigate('/hesap/para-yatir?view=history', { replace: true })}
              >
                Yatırımlarımı Gör
              </button>
            </div>
          ) : isApiProvider && transaction && !hostedPaymentUrl ? (
            <button
              type="button"
              className="gold-btn w-full py-3.5 rounded-xl mt-5"
              onClick={() => navigate('/hesap/para-yatir?view=history', { replace: true })}
            >
              Yatırımlarımı Gör
            </button>
          ) : (
            <button
              type="button"
              className="gold-btn w-full py-3.5 rounded-xl mt-5 flex items-center justify-center gap-2 disabled:opacity-60"
              disabled={busy}
              {...accountHook('submit')}
              onClick={confirmPayment}
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy
                ? (isApiProvider && !transaction
                  ? 'Ödeme sağlayıcısına bağlanılıyor...'
                  : 'Bildiriliyor...')
                : isPaylux || isExpay
                  ? `${method.name} ile Devam Et`
                : isPrimevpay
                  ? (transaction ? 'Ödeme Sayfasını Aç' : `${method.name} ile Devam Et`)
                : isHemenSystem
                  ? 'Hemen Havale ile Devam Et'
                  : isHera
                    ? (transaction && hostedPaymentUrl
                      ? 'Ödeme Sayfasını Aç'
                      : `${method.name} ile Devam Et`)
                    : isWizkasa
                      ? (transaction ? 'Ödeme Sayfasını Aç' : 'Wizkasa ile Devam Et')
                      : isPayurus
                        ? (transaction ? 'Ödeme Sayfasını Aç' : 'Payurus ile Devam Et')
                        : isVevoPay
                          ? `${method.name} ile Devam Et`
                        : isLinkHavale
                          ? (transaction
                            ? 'Ödeme Sayfasını Aç'
                            : `${method.name} ile Devam Et`)
                          : isUltraPay
                          ? (ultrapayIsH2H
                            ? `${method.name} ile Devam Et`
                            : (transaction
                              ? 'Ödeme Sayfasını Aç'
                              : 'UltraPay ile Devam Et'))
                          : cryptoboxIsHosted
                            ? (transaction
                              ? 'Ödeme Sayfasını Aç'
                              : 'CryptoBox ile Devam Et')
                          : isCashMoney
                            ? (transaction
                              ? 'Ödeme Sayfasını Aç'
                              : `${method.name} ile Devam Et`)
                          : 'Ödemeyi Yaptım'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// VEVO PAY yatırımda bu işleme atanmış hesabı ve/veya ödeme sayfasının
// adresini döndürür. Hesap geldiyse ikisi de gösterilir ve oyuncu isterse
// kendi bankasından havale eder — o yüzden orada otomatik yönlendirme yoktur.
// Hesapsız (hosted) cevapta ekranda bağlantıdan başka bir şey kalmadığı için
// ödeme sayfası kendiliğinden açılır. Komisyon alanları (`fee`/`credited`)
// bayinin alacağıdır ve oyuncuya hiç gelmez.
const VevoPayPaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
        ödemenizi tamamlayabilirsiniz.
      </div>
    );
  }
  return (
    <>
      {payment.bank_account.bank && (
        <Detail label="Banka" value={payment.bank_account.bank} />
      )}
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {payment.expires_at && (
        <Detail
          label="Son kullanım"
          value={new Date(payment.expires_at).toLocaleString('tr-TR')}
        />
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba yapın. Hesap bu işleme özeldir ve
        süresi doldurulduğunda talep kendiliğinden kapanır.
      </div>
    </>
  );
};

// CashMoney Pay yatırımda bu işleme atanmış hesabı döndürür; hesap yerine
// ödeme sayfası adresi gelirse ekranda bağlantıdan başka bir şey kalmaz.
const CashMoneyPaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        {payment.link
          ? 'Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak ödemenizi tamamlayabilirsiniz.'
          : 'Ödeme bilgileri henüz oluşmadı. Lütfen birazdan tekrar deneyin ya da canlı destekle iletişime geçin.'}
      </div>
    );
  }
  return (
    <>
      {payment.bank_account.bank && (
        <Detail label="Banka" value={payment.bank_account.bank} />
      )}
      {payment.bank_account.holder && (
        <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      )}
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {payment.expires_at && (
        <Detail
          label="Son kullanım"
          value={new Date(payment.expires_at).toLocaleString('tr-TR')}
        />
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve talep ettiğiniz tutar kadar yapın.
        Ödemeniz sağlayıcı tarafından onaylandığında bakiyenize kendiliğinden
        yansır.
      </div>
    </>
  );
};

// Paylux yatırımda bu işleme atanmış hesabı ve **ödenecek tutarı** döndürür.
// Tutar ayrıca yazılır çünkü sağlayıcı havaleyi onunla eşleştirir ve talep
// edilenden farklı olabilir: oyuncu kendi yazdığı tutarı yollarsa ödeme
// eşleşmez ve yatırım beklemede kalır.
const PayluxPaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme bilgileri henüz oluşmadı. Lütfen birazdan tekrar deneyin ya da
        canlı destekle iletişime geçin.
      </div>
    );
  }
  return (
    <>
      {payment.amount > 0 && (
        <Detail
          label="Gönderilecek tutar"
          value={`${Number(payment.amount).toLocaleString('tr-TR')} ₺`}
        />
      )}
      {payment.bank_account.holder && (
        <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      )}
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {payment.fast && (
        <Detail label="Gönderim tipi" value="FAST ile gönderebilirsiniz" />
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve <strong>yukarıdaki tutar kadar</strong>
        {' '}yapın; farklı bir tutar gönderilirse ödeme eşleşmez. Havale
        gönderen adı hesabınızdaki ad soyad ile aynı olmalıdır. Ödemeniz
        onaylandığında bakiyenize kendiliğinden yansır.
      </div>
    </>
  );
};

// ExPay H2H akışında işleme atanan IBAN'ı ve alıcı adını, ödeme sayfası
// akışında yalnızca sayfanın adresini döndürür. Havale gönderen adı
// sağlayıcıya iletilen kimlikle (ad, soyad, TC) eşleşmelidir.
const ExpayPaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel ödeme bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        {payment.link
          ? 'Ödemeyi ExPay ödeme sayfasında tamamlayın. Sayfa açılmadıysa aşağıdaki butonu kullanın.'
          : 'Ödeme bilgileri henüz oluşmadı. Lütfen birazdan tekrar deneyin ya da canlı destekle iletişime geçin.'}
      </div>
    );
  }
  return (
    <>
      {payment.bank_account.holder && (
        <Detail label="Alıcı" value={payment.bank_account.holder} copy />
      )}
      {payment.bank_account.bank && (
        <Detail label="Banka" value={payment.bank_account.bank} />
      )}
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve talep ettiğiniz tutar kadar yapın.
        Havaleyi kendi adınıza kayıtlı hesabınızdan gönderin; ödemeniz
        onaylandığında bakiyenize kendiliğinden yansır.
      </div>
    </>
  );
};

// Hemen Havale yatırımda **hem** oyuncuya atanmış IBAN'ı hem de ödeme
// bağlantısını döndürür. İkisi birden gösterilir: oyuncu isterse kendi
// bankasından havale eder, isterse sağlayıcının sayfasını kullanır.
// Havale akışında bu işleme özel IBAN gösterilir; kart akışında yalnızca
// sağlayıcının 3D ödeme sayfasının adresi döner (kart bilgileri bizim
// sunucumuzdan geçmez).
const HemenSystemPaymentDetails = ({ payment, creditCard = false }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        {creditCard ? (
          <>
            Güvenli kart ödeme sayfanızı oluşturmak için aşağıdaki butona
            basın. Kart bilgileriniz yalnızca bankanın 3D Secure sayfasında
            girilir.
          </>
        ) : (
          <>
            Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona
            basın. Bilgiler oluşmadan transfer yapmayın.
          </>
        )}
      </div>
    );
  }
  if (creditCard) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
        kart ödemenizi tamamlayabilirsiniz. 3D doğrulaması bittiğinde bakiyeniz
        kendiliğinden yüklenir.
      </div>
    );
  }
  if (!payment.bank_account) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Aşağıdaki düğmeye dokunarak ödemenizi
        tamamlayabilirsiniz.
      </div>
    );
  }
  return (
    <>
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve yalnızca belirtilen tutarda yapın.
        Hesap bu işleme özeldir ve 10 dakika geçerlidir.
      </div>
    </>
  );
};

// UltraPay'de akış yönteme göre ikiye ayrılır: H2H havale (yöntem 42) alıcı
// hesabı doğrudan cevapta döndürür ve burada gösterilir, kalan yöntemler
// sağlayıcının kendi ödeme sayfasına yönlendirir. Tutar tam sayı olmalıdır;
// kuruşlu talep sağlayıcıya hiç gönderilmez.
const UltraPayPaymentDetails = ({ payment, bankTransfer }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        {bankTransfer
          ? 'Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona basın. Bilgiler oluşmadan transfer yapmayın.'
          : 'Ödeme sayfasını açmak için aşağıdaki butona basın.'}
      </div>
    );
  }
  if (!payment.bank_account) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Aşağıdaki düğmeye dokunarak ödemenizi
        tamamlayabilirsiniz.
      </div>
    );
  }
  return (
    <>
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve yalnızca belirtilen tutarda yapın.
        Hesap bu işleme özeldir.
      </div>
    </>
  );
};

// Hera havale yatırımında (H2H) atanmış hesabı doğrudan döndürür; kart
// akışlarında yalnızca barındırılan ödeme sayfasının adresini verir.
//
// Sağlayıcının `note` alanı (FAST uyarısı gibi) **aynen** gösterilir:
// gizlenirse oyuncunun havalesi eşleşmez ve yatırım askıda kalır.
const HeraPaymentDetails = ({ payment, bankTransfer }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        {bankTransfer
          ? 'Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona basın. Bilgiler oluşmadan transfer yapmayın.'
          : 'Ödeme sayfasını açmak için aşağıdaki butona basın.'}
      </div>
    );
  }
  if (!payment.bank_account) {
    return (
      <>
        <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
          Ödeme sayfası hazır. Aşağıdaki düğmeye dokunarak ödemenizi
          tamamlayabilirsiniz.
        </div>
        {payment.note && (
          <div className="pusu-card p-4 text-xs text-amber-200 whitespace-pre-wrap" {...accountHook('notice')}>
            {payment.note}
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <Detail label="Banka" value={payment.bank_account.bank_name} />
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {payment.bank_account.amount ? (
        <Detail
          label="Gönderilecek tutar"
          value={`${Number(payment.bank_account.amount).toLocaleString('tr-TR')} ₺`}
        />
      ) : null}
      {payment.note && (
        <div className="pusu-card p-4 text-xs text-amber-200 whitespace-pre-wrap" {...accountHook('notice')}>
          {payment.note}
        </div>
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve yalnızca belirtilen tutarda yapın.
        Hesap bu işleme özeldir ve 10 dakika geçerlidir.
      </div>
    </>
  );
};

// Payurus ödemesi yalnızca sağlayıcının kendi sayfasında tamamlanır; bize
// dönen tek bilgi o sayfanın adresidir.
// Link Havale ödemesi tamamen sağlayıcının sayfasında tamamlanır; hesap
// bilgisi, kart formu ve kripto adresi orada sorulur. Bağlantı **tek
// kullanımlıktır ve 30 dakika geçerlidir**, iframe'e konmaz.
const LinkHavalePaymentDetails = ({ payment }) => {
  if (!payment?.link) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfanızı oluşturmak için aşağıdaki butona basın. Sayfa
        oluşmadan ödeme yapmayın.
      </div>
    );
  }
  return (
    <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
      Ödeme sayfası hazır ve 30 dakika geçerlidir. Yönlendirilmezseniz
      aşağıdaki düğmeye dokunarak ödemenizi tamamlayabilirsiniz. Ödeme
      tamamlandığında bakiyeniz kendiliğinden yüklenir.
    </div>
  );
};

// HızlıcaParalar ödemesi tamamen sağlayıcının sayfasında tamamlanır: hesap
// bilgisi, kart formu ve kripto adresi orada sorulur. **Tutar da orada
// yazılır** — sağlayıcının yatırım isteği tutar almaz — ve bakiyeye giren
// para sağlayıcının bildirdiğidir; oyuncu burada girdiğinden farklı bir tutar
// ödeyebilir.
/**
 * Hızlı Havale ödeme bilgisi.
 *
 * İki akış aynı bileşenden çizilir ve ayrım **cevabın kendisindedir**: hesap
 * geldiyse IBAN ekranda durur (yönlendirme yapılmaz — duran hesap bilgisini
 * kaybettirirdi), gelmediyse ekranda tutulacak bir şey yoktur ve oyuncu
 * sağlayıcının sayfasına gider. VEVO PAY ile aynı gövde şeklini paylaşırlar,
 * bu yüzden hesap kontrolü de ortak yardımcıdan gelir.
 */
const HizliHavalePaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel hesap bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
        ödemenizi tamamlayabilirsiniz.
      </div>
    );
  }
  return (
    <>
      {payment.bank_account.bank && (
        <Detail label="Banka" value={payment.bank_account.bank} />
      )}
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {payment.expires_at && (
        <Detail
          label="Son kullanım"
          value={new Date(payment.expires_at).toLocaleString('tr-TR')}
        />
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve <strong>tam olarak yukarıdaki
        tutarla</strong> yapın: eşleştirme tutar üzerinden yapılır. Hesap bu
        işleme özeldir ve süre dolduğunda talep kendiliğinden kapanır.
      </div>
    </>
  );
};

// Zelvoq'un iki akışı tek bileşende çizilir: hesap varsa IBAN, yoksa ödeme
// sayfası. Ayrım cevabın kendisindedir — aynı kurulumda havale hesap akışıyla,
// kart ödeme sayfasıyla gelir.
// CryptoBox: zincire özel adres (ya da ödeme sayfası). Adres üretimi
// sağlayıcıda eşzamansızdır; ilk cevapta hazır değilse oyuncu yeniden sorar
// ("Adresi Yenile") — doküman bunu açıkça önerir.
const CryptoBoxPaymentDetails = ({ payment, transactionId, onRefreshed }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const crypto = payment?.crypto;
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Size özel ödeme bilgilerini oluşturmak için aşağıdaki butona basın.
      </div>
    );
  }
  if (!crypto) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunun;
        kripto parayı ve ağı o sayfada seçin.
      </div>
    );
  }
  const refresh = async () => {
    if (!transactionId || refreshing) return;
    setRefreshing(true);
    setRefreshError('');
    try {
      const { data } = await client.post(
        `/cryptobox/deposit/${transactionId}/address`);
      if (data.transaction) onRefreshed?.(data.transaction);
    } catch (error) {
      setRefreshError(error.response?.data?.detail || 'Adres alınamadı.');
    } finally {
      setRefreshing(false);
    }
  };
  if (!crypto.ready) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300 space-y-3" {...accountHook('notice')}>
        <p>
          {crypto.label} adresiniz hazırlanıyor. Birkaç saniye sonra yenileyin;
          adres görünmeden gönderim yapmayın.
        </p>
        <button
          type="button"
          className="provider-chip flex items-center gap-2"
          onClick={refresh}
          disabled={refreshing}
        >
          {refreshing && <Loader2 size={14} className="animate-spin" />}
          Adresi Yenile
        </button>
        {refreshError && <p className="text-xs text-rose-300">{refreshError}</p>}
      </div>
    );
  }
  const quote = crypto.quote?.amount ? crypto.quote : null;
  return (
    <>
      <Detail label="Kripto / Ağ" value={`${crypto.asset} · ${crypto.network}`} />
      {/* Miktar kopyalanabilir: oyuncu onu cüzdanına elle yazacak ve sekiz
          haneli bir sayıyı yanlış yazmak eksik gönderim demektir. */}
      {quote && (
        <Detail
          label="Gönderilecek miktar"
          value={`${quote.amount} ${quote.asset}`}
          copy
        />
      )}
      <Detail label="Cüzdan adresi" value={crypto.address} copy />
      {crypto.destination_tag && (
        <Detail label="Hedef etiketi (Destination Tag)" value={crypto.destination_tag} copy />
      )}
      <div className="pusu-card p-4 text-xs text-amber-300/90" {...accountHook('notice')}>
        Bu adrese <strong>yalnızca {crypto.asset} ({crypto.network})</strong>
        {' '}gönderin; başka ağdan yapılan gönderim geri alınamaz.
        {crypto.destination_tag && ' Hedef etiketini yazmadan gönderim yapmayın.'}
      </div>
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Adres size özeldir ve kalıcıdır. Hesabınıza, gönderdiğiniz tutarın
        ödeme bildirildiği anki TL karşılığı eklenir; yukarıdaki miktar
        {' '}<strong>yaklaşıktır</strong> — kur gönderim anına kadar
        değişebilir. Eksik ya da fazla gönderim de kabul edilir, bakiyenize
        gönderdiğiniz kadarı geçer.
      </div>
      {quote && (
        <button
          type="button"
          className="provider-chip flex items-center gap-2"
          onClick={refresh}
          disabled={refreshing}
        >
          {refreshing && <Loader2 size={14} className="animate-spin" />}
          Miktarı Güncelle
        </button>
      )}
      {refreshError && <p className="text-xs text-rose-300">{refreshError}</p>}
    </>
  );
};

const ZelvoqPaymentDetails = ({ payment }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel ödeme bilgilerini oluşturmak için aşağıdaki butona
        basın. Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (!hasBankAccount(payment)) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
        ödemenizi tamamlayabilirsiniz. Tutarı ödeme sayfasında onaylayın.
      </div>
    );
  }
  return (
    <>
      {payment.bank_account.bank && (
        <Detail label="Banka" value={payment.bank_account.bank} />
      )}
      <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
      <Detail label="IBAN" value={payment.bank_account.iban} copy />
      {/* Sağlayıcının kendi açıklama satırı: farklı isimden yapılan
          yatırımın kabul edilmediğini o satır söyler, bu yüzden gizlenmez. */}
      {payment.bank_account.note && (
        <div className="pusu-card p-4 text-xs text-amber-300/90" {...accountHook('notice')}>
          {payment.bank_account.note}
        </div>
      )}
      <div className="pusu-card p-4 text-xs text-gray-400" {...accountHook('notice')}>
        Transferi yalnızca bu hesaba ve <strong>tam olarak yukarıdaki
        tutarla</strong> yapın: eşleştirme tutar üzerinden yapılır. Hesap bu
        işleme özeldir ve süre dolduğunda talep kendiliğinden kapanır.
      </div>
    </>
  );
};

const HizlicaParalarPaymentDetails = ({ payment }) => {
  if (!payment?.link) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfanızı oluşturmak için aşağıdaki butona basın. Sayfa
        oluşmadan ödeme yapmayın.
      </div>
    );
  }
  return (
    <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
      Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
      ödemenizi tamamlayabilirsiniz. Tutarı ödeme sayfasında onaylayın; ödeme
      tamamlandığında bakiyeniz kendiliğinden yüklenir.
    </div>
  );
};

// PrimeVpay ödeme sayfası atanan IBAN'ı, alıcı adını ve 15 dakikalık süreyi
// gösterir; havale sonrası "ödemeyi yaptım" da orada işaretlenir.
const PrimevpayPaymentDetails = ({
  payment, payerName = '', payerNameValid = false, onPayerNameChange,
}) => {
  if (!payment?.link) {
    const showHint = payerName.trim() !== '' && !payerNameValid;
    return (
      <>
        <label className="block text-sm text-gray-300" {...accountHook('field', 'payer-name')}>
          Havaleyi gönderecek hesabın sahibi
          <input
            type="text"
            name="payer_name"
            autoComplete="name"
            maxLength={128}
            value={payerName}
            onChange={(e) => onPayerNameChange?.(e.target.value)}
            placeholder="Ad Soyad"
            aria-invalid={showHint}
            aria-describedby="payer-name-hint"
            className="pusu-input mt-1"
          />
        </label>
        <p id="payer-name-hint" className="text-xs text-gray-400">
          {showHint
            ? 'Adınızı ve soyadınızı birlikte yazın.'
            : 'Ödemeniz bu adla eşleştirilir; havaleyi yalnızca bu kişiye ait hesaptan gönderin.'}
        </p>
        <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
          Ödeme sayfanızı oluşturmak için aşağıdaki butona basın. Sayfa
          oluşmadan ödeme yapmayın.
        </div>
      </>
    );
  }
  return (
    <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
      Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki butona dokunun.
      Havaleyi sayfada gösterilen hesaba 15 dakika içinde yapın ve sayfadaki
      &quot;ödemeyi yaptım&quot; adımını tamamlayın; ödemeniz onaylandığında
      bakiyenize kendiliğinden yansır.
    </div>
  );
};

const PayurusPaymentDetails = ({ payment }) => {
  if (!payment?.link) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfanızı oluşturmak için aşağıdaki butona basın. Sayfa
        oluşmadan ödeme yapmayın.
      </div>
    );
  }
  return (
    <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
      Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye dokunarak
      ödemenizi tamamlayabilirsiniz. Ödeme tamamlandığında bakiyeniz
      kendiliğinden yüklenir; sayfayı kapatmadan işlemi bitirin.
    </div>
  );
};

// Wizkasa kanala göre farklı bilgi döner: LINK'te hosted sayfa adresi,
// API'de hesap bilgisi (banka / Papara / kripto adresi).
const WizkasaPaymentDetails = ({ payment, methodName }) => {
  if (!payment) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Bu işleme özel ödeme bilgilerini oluşturmak için aşağıdaki butona basın.
        Bilgiler oluşmadan transfer yapmayın.
      </div>
    );
  }
  if (payment.link) {
    return (
      <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
        Ödeme sayfası hazır. Yönlendirilmezseniz aşağıdaki düğmeye
        dokunarak {methodName} ödemenizi tamamlayabilirsiniz.
      </div>
    );
  }
  if (payment.bank_account) {
    return (
      <>
        <Detail label="Banka" value={payment.bank_account.bank_name} />
        <Detail label="Hesap sahibi" value={payment.bank_account.holder} />
        <Detail label="IBAN" value={payment.bank_account.iban} copy />
      </>
    );
  }
  if (payment.papara_account) {
    return (
      <>
        <Detail label="Hesap sahibi" value={payment.papara_account.holder} />
        <Detail
          label="Papara numarası"
          value={payment.papara_account.account_number}
          copy
        />
      </>
    );
  }
  if (payment.crypto) {
    return (
      <>
        <Detail
          label="Kripto / Ağ"
          value={`${payment.crypto.coin} · ${payment.crypto.network}`}
        />
        <Detail label="Cüzdan adresi" value={payment.crypto.address} copy />
        {payment.expires_at && (
          <Detail
            label="Son kullanım"
            value={new Date(payment.expires_at).toLocaleString('tr-TR')}
          />
        )}
      </>
    );
  }
  return (
    <div className="pusu-card p-4 text-sm text-gray-300" {...accountHook('notice')}>
      Ödeme bilgileri hazırlanıyor. Birkaç saniye sonra yatırım geçmişinizden
      durumu takip edebilirsiniz.
    </div>
  );
};

// Satırın alan adı etiketten türer (`Hesap sahibi` → `hesap-sahibi`,
// `IBAN` → `iban`): panelden yazılan CSS tek bir satırı
// `[data-bhs-account="detail"][data-bhs-field="iban"]` ile seçebilsin diye.
// Etiket değişirse ad da değişir — ekranda yazan da değişmiştir.
const detailField = (label) => String(label || '')
  .toLocaleLowerCase('tr-TR')
  .replace(/ı/g, 'i').replace(/ğ/g, 'g').replace(/ü/g, 'u')
  .replace(/ş/g, 's').replace(/ö/g, 'o').replace(/ç/g, 'c')
  .replace(/\([^)]*\)/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const Detail = ({ label, value, copy = false }) => {
  const { toast } = useToast();

  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Kopyalandı', description: `${label} panoya kopyalandı.` });
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Bilgiyi elle seçip kopyalayabilirsiniz.' });
    }
  };

  return (
    <div
      className="pusu-card p-3 flex items-center justify-between gap-3"
      {...accountHook('detail', detailField(label))}
    >
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div
          className="font-semibold break-all"
          data-selectable={copy ? 'true' : undefined}
        >
          {value || '—'}
        </div>
      </div>
      {copy && value && (
        <button
          type="button"
          className="provider-chip shrink-0 flex items-center gap-1.5"
          {...accountHook('copy')}
          onClick={copyValue}
        >
          <Copy size={14} aria-hidden="true" />
          Kopyala
        </button>
      )}
    </div>
  );
};

export default DepositPayment;
